/**
 * Pull a human-readable message out of whichever error shape the
 * backend used.
 *
 * Lives in its own module (not `api.ts`) so the production
 * `ApiService` class isn't co-located with a pure helper exported
 * only for testability — Codex review feedback on PR #12754
 * (Gap #21 in `Entity Registry - Plan + Lean POC.md`).
 *
 * Five known shapes:
 *
 *   1. FastAPI / Pydantic — `{detail: "<string>"}` (or an array of
 *      validation errors `[{loc, msg, type}, ...]`).
 *   2. NestJS `OpenAiErrorFilter` envelope (every `/v1/*` route) —
 *      `{error: {message: "<string>", type, code, param}}`. The bug
 *      Gap #21 fixes: pre-fix the caller treated `errorData.error`
 *      as a string and rendered `[object Object]`.
 *   3. Default NestJS `HttpException` body (every un-filtered `/v2/*`
 *      route) — `{message: "<the real reason>", error: "Bad Request",
 *      statusCode}`. Here `error` is just the HTTP STATUS NAME —
 *      `message` carries the text the user needs. Checked BEFORE the
 *      bare-`error` shape: pre-fix, shape (4) won and users saw a
 *      literal "Bad Request"/"Conflict" instead of the reason (live on
 *      dev 2026-07-13: workflow generate 400s rendered "Bad Request",
 *      hiding "…unless workflow authoring is enabled"). NestJS
 *      ValidationPipe emits `message` as a string ARRAY — joined.
 *   4. Legacy NestJS — `{error: "<string>"}` bare string with no
 *      usable `message` alongside it.
 *   5. Legacy bare `{message: "<string>"}` (`HttpException` default
 *      when constructed with a string).
 *
 * Returns `null` when the payload doesn't carry a recognizable
 * message — caller falls back to `response.statusText`.
 */
export function extractErrorMessage(errorData: unknown): string | null {
  if (!errorData || typeof errorData !== 'object') return null
  const data = errorData as Record<string, unknown>

  // (1) FastAPI `detail` — string or validation-error array.
  if (typeof data.detail === 'string') return data.detail
  if (Array.isArray(data.detail)) {
    const parts = data.detail
      .map(formatValidationDetailEntry)
      .filter((s): s is string => s !== null)
    if (parts.length > 0) return parts.join('; ')
  }

  // (2) OpenAI envelope — `error` is an object with `.message`.
  if (data.error && typeof data.error === 'object') {
    const inner = data.error as { message?: unknown }
    if (typeof inner.message === 'string') return inner.message
  }

  // (3) Default NestJS HttpException body: `error` is the status NAME
  // ("Bad Request") and `message` is the real reason — prefer `message`.
  if (typeof data.error === 'string') {
    if (typeof data.message === 'string' && data.message.trim().length > 0) {
      return data.message
    }
    if (Array.isArray(data.message)) {
      const parts = data.message.filter(
        (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0,
      )
      if (parts.length > 0) return parts.join('; ')
    }
    // (4) Legacy `error` as a bare string (no usable message alongside).
    return data.error
  }

  // (5) Legacy `message` as a bare string.
  if (typeof data.message === 'string') return data.message

  return null
}

/**
 * Format one FastAPI validation-error entry as `"<path>: <msg>"`.
 * Pre-Codex-#12754 we joined just `msg` and dropped `loc`, losing
 * the field path:
 *
 *   pre:  `field required; must be int`
 *   post: `body.x: field required; body.y: must be int`
 *
 * `loc` is `["body", "x", "nested", "y"]` style; we drop the first
 * segment when it's the redundant request-section marker
 * (`body`/`query`/`path`/`header`/`cookie`) since the route is
 * already known to the caller, but keep it if it doesn't look like
 * one of those markers (older FastAPI versions, custom resolvers).
 */
function formatValidationDetailEntry(entry: unknown): string | null {
  if (typeof entry === 'string') return entry
  if (!entry || typeof entry !== 'object') return null
  const e = entry as { msg?: unknown; loc?: unknown }
  if (typeof e.msg !== 'string') return null
  const loc = formatLoc(e.loc)
  return loc ? `${loc}: ${e.msg}` : e.msg
}

const REQUEST_SECTION_MARKERS = new Set(['body', 'query', 'path', 'header', 'cookie'])

function formatLoc(loc: unknown): string | null {
  if (!Array.isArray(loc) || loc.length === 0) return null
  const segments = loc
    .map((s) => (typeof s === 'string' || typeof s === 'number' ? String(s) : null))
    .filter((s): s is string => s !== null && s.length > 0)
  if (segments.length === 0) return null
  // Drop the leading request-section marker so the user sees
  // `x: field required` instead of `body.x: field required` — the
  // route is already known.
  if (segments.length > 1 && REQUEST_SECTION_MARKERS.has(segments[0]!)) {
    return segments.slice(1).join('.')
  }
  return segments.join('.')
}
